/**
 * ExportMenu — per-survey export dropdown (CSV / Excel / JSON) for survey results.
 *
 * A single branded button that opens a menu of the three formats. Kept dumb: it
 * just calls the export utilities in exportSurvey.ts against the given CompletedSurvey.
 */
import React, { FC, useState } from "react";
import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { DataObjectOutlined, DescriptionOutlined, FileDownloadOutlined, GridOnOutlined } from "@mui/icons-material";
import type { CompletedSurvey } from "./surveyResults.mock";
import { exportSurveyCsv, exportSurveyJson, exportSurveyXlsx } from "./exportSurvey";

type ExportMenuProps = {
    completed: CompletedSurvey;
    /** "button" = labelled outlined button (detail header); "icon" = compact icon (list rows). */
    variant?: "button" | "icon";
};

export const ExportMenu: FC<ExportMenuProps> = ({ completed, variant = "button" }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const close = () => setAnchorEl(null);
    const run = (fn: (c: CompletedSurvey) => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        fn(completed);
        close();
    };

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    return (
        <>
            {variant === "icon" ? (
                <Tooltip title="Export">
                    <IconButton size="small" onClick={openMenu} aria-label="Export survey">
                        <FileDownloadOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : (
                <Button variant="outlined" color="primary" startIcon={<FileDownloadOutlined />} onClick={openMenu}>
                    Export
                </Button>
            )}
            <Menu anchorEl={anchorEl} open={open} onClose={close}>
                <MenuItem onClick={run(exportSurveyCsv)}>
                    <ListItemIcon>
                        <DescriptionOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>CSV</ListItemText>
                </MenuItem>
                <MenuItem onClick={run(exportSurveyXlsx)}>
                    <ListItemIcon>
                        <GridOnOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Excel (.xlsx)</ListItemText>
                </MenuItem>
                <MenuItem onClick={run(exportSurveyJson)}>
                    <ListItemIcon>
                        <DataObjectOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>JSON</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};
